import React, { useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import {
  PrintModal,
  useAppContext,
  useModal,
  useQueryGetCourseVerification,
  useQueryGetObjectivesTree,
} from "@examspark/common-app";
import { subjectMeta } from "@examspark/common-meta";
import {
  Modal as BaseModal,
  Button,
  CautionBanner,
  CheckboxGridInput,
  HSpace,
  RouterLink,
  TextInput,
  VSpace,
} from "@examspark/common-ui";
import {
  IconDocumentLine,
  IconGearLine,
} from "@examspark/common-ui/dist/components/icons";
import { LabelText } from "@examspark/common-ui/dist/components/inputs/Label";
import {
  ObjectiveBadge,
  ObjectiveCategoryBadge,
  ObjectiveCurriculumUsageBadge,
  VerificationLegend,
  VerificationRowView,
  VerificationSectionRow,
  VerificationTable,
  createVerificationView,
} from "@examspark/customer/tcmpc";
import {
  TeksCategory,
  TeksCategoryList,
  TeksCategoryMeta,
} from "@examspark/types";

import { WindowContext } from "../../components/common/AppWrapper";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import PageHead from "../../components/common/PageHead";
import PageMenu from "../../components/common/PageMenu";
import PageTitle from "../../components/common/PageTitle";
import { useFlags } from "../../hooks/useFlags";
import { useQuerySearchBonfireObjectives } from "../../hooks/useQueries";
import { useUsername } from "../../hooks/useUsername";

const FilterBar = styled.div`
  position: absolute;
  bottom: 1.5rem;
  left: 1.5rem;
  width: calc(100% - 3rem);
  right: 1.5rem;
  display: flex;
  background-color: #fff;
  border-radius: 0.5rem;
  box-sizing: border-box;
  background-color: #fff;
  border: solid 1px #ddd;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 0 10px 5px rgba(0, 0, 0, 0.05);

  input {
    flex: 1;
  }
`;

const FilterBarTable = styled.div`
  display: flex;
  border-radius: 0.5rem;
  box-sizing: border-box;
  gap: 0.2rem;

  input {
    max-width: 30rem;
    flex: 1;
  }
`;

const StyledCenteredCell = styled.td`
  text-align: center;
`;

const StyledCheckboxGrid = styled(CheckboxGridInput)`
  border: none;
  grid-template-columns: repeat(auto-fill, minmax(25rem, 1fr));

  ${LabelText} {
    text-transform: none;
  }
`;

const StyledVerificationTable = styled(VerificationTable)`
  td:first-child {
    border-left: none;
  }

  td {
    border-right: none;
  }
`;

const CourseVerificationRoute = () => {
  const params = useParams<{
    id: string;
  }>();
  const win = useContext(WindowContext);
  const [filterText, setFilterText] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<TeksCategory[]>();
  const printModal = useModal();
  const flags = useFlags();
  const [appContext] = useAppContext();
  const username = useUsername();

  const { data } = useQueryGetCourseVerification(params.id);
  const { data: objectives } = useQuerySearchBonfireObjectives(
    data?.alignmentScopes ?? undefined,
  );

  const { data: sectionObjectives } = useQueryGetObjectivesTree(
    objectives,
    "section",
  );

  const subject = data?.subject?.[0];

  const view = useMemo(() => {
    if (data == null || sectionObjectives == null) return undefined;
    return createVerificationView(
      data,
      sectionObjectives,
      filterText,
      selectedFilters,
    );
  }, [data, filterText, sectionObjectives, selectedFilters]);

  if (data !== undefined && !data.alignmentScopes?.length) {
    return (
      <CautionBanner>
        Cannot load data because Course is not configured with any objective
        scopes.
      </CautionBanner>
    );
  }

  const mobile = win.device === "phone";

  const filterControls = (
    <>
      <TextInput
        placeholder="Filter Text"
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        autoCapitalize="off"
      />
      <Button title="Settings" variant="plain">
        <IconGearLine width="16" onClick={() => setIsFilterModalOpen(true)} />
      </Button>
    </>
  );

  return (
    <>
      <PrintModal
        onRequestPrint={() => ({
          action: "PrintTcmpcVerificationDocument",
          params: {
            courseId: data?.id,
            username: username,
            workspaceId: appContext.workspaceUser?.id,
          },
          label: "TVD",
        })}
        requestPrintDisabled={data == null}
        title="Download TVD as PDF"
        {...printModal.modalProps}
      />
      <BaseModal
        size="xsmall"
        title="Filter View"
        isOpen={isFilterModalOpen}
        onRequestClose={() => setIsFilterModalOpen(false)}
        buttons={
          <Button title="Close" onClick={() => setIsFilterModalOpen(false)}>
            Close
          </Button>
        }
      >
        <BaseModal.PaddedBody>
          <VSpace>
            <StyledCheckboxGrid
              value={selectedFilters}
              onChange={(selected) => setSelectedFilters(selected)}
            >
              {TeksCategoryList.map((category) => (
                <CheckboxGridInput.Item key={category} value={category}>
                  {TeksCategoryMeta[category].label} Standards
                </CheckboxGridInput.Item>
              ))}
            </StyledCheckboxGrid>
          </VSpace>
        </BaseModal.PaddedBody>
      </BaseModal>
      <VSpace size="3rem" style={{ paddingBottom: "3rem" }}>
        <PageHead>
          <Breadcrumbs
            items={[
              ...Breadcrumbs.Dashboard(),
              <RouterLink key="courses" to="/browse">
                Courses
              </RouterLink>,
              subject ? (
                <RouterLink key="subject" to="/browse">
                  {
                    subjectMeta[data?.subject?.[0] as keyof typeof subjectMeta]
                      ?.label
                  }
                </RouterLink>
              ) : null,
              <RouterLink key="course" to={`/course/${params.id}`}>
                {data?.name}
              </RouterLink>,
            ]}
          />
          <PageTitle
            tools={
              <PageMenu
                buttons={[
                  flags.printCourseTvdEnabled && {
                    title: "Download as PDF",
                    onClick: () => printModal.setIsOpen(true),
                    icon: <IconDocumentLine />,
                  },
                ]}
              />
            }
          >
            TEKS Verification Document
          </PageTitle>
        </PageHead>
        {view != null && (
          <>
            <VerificationLegend />
            <StyledVerificationTable cellSpacing={0}>
              <thead>
                <tr>
                  {!mobile && (
                    <th>
                      <FilterBarTable>{filterControls}</FilterBarTable>
                    </th>
                  )}
                  {view?.headers.map((unit, index) => (
                    <th key={unit.id}>
                      <RouterLink to={`/unit/${unit.id}`}>
                        Unit / Module <br />
                        {unit.numberLabel ?? index + 1}
                      </RouterLink>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {view?.rows.map((row) => {
                  switch (row.type) {
                    case "scope":
                      return (
                        <VerificationSectionRow key={row.objective.sk}>
                          <td colSpan={view.width + (mobile ? 0 : 1)}>
                            {row.objective.content}
                          </td>
                        </VerificationSectionRow>
                      );
                    case "knowledge_statement":
                      return (
                        <VerificationSectionRow key={row.objective.id}>
                          <td colSpan={view.width + (mobile ? 0 : 1)}>
                            <div style={{ display: "inline-block" }}>
                              <ObjectiveBadge objective={row.objective} />
                            </div>
                            <div
                              style={{
                                height: "3.0em",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {row.objective.content}
                            </div>
                          </td>
                        </VerificationSectionRow>
                      );
                  }

                  return (
                    <Row key={row.objective.id} row={row} mobile={mobile} />
                  );
                })}
              </tbody>
            </StyledVerificationTable>
          </>
        )}
        {win.device === "phone" && <FilterBar>{filterControls}</FilterBar>}
      </VSpace>
    </>
  );
};

interface RowProps {
  row: Extract<VerificationRowView, { type: "standard_row" }>;
  mobile?: boolean;
}

const Row: React.FC<React.PropsWithChildren<RowProps>> = ({ row, mobile }) => {
  const usageCells = row.usages.map((u, i) => (
    <StyledCenteredCell key={i}>
      <ObjectiveCurriculumUsageBadge value={u} />
    </StyledCenteredCell>
  ));

  const children = (
    <>
      <HSpace>
        <div style={{ display: "inline-block" }}>
          <ObjectiveBadge objective={row.objective} />{" "}
        </div>
        <div style={{ display: "inline-block" }}>
          <ObjectiveCategoryBadge value={row.category} />
        </div>
      </HSpace>
      <div style={{ color: "#555" }}>{row.objective.content}</div>
    </>
  );

  if (mobile) {
    return (
      <>
        <tr>
          <td colSpan={row.usages.length} />
        </tr>
        <tr>{usageCells}</tr>
      </>
    );
  }

  return (
    <tr>
      <td>{children}</td>
      {usageCells}
    </tr>
  );
};

export default CourseVerificationRoute;
